import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import OrderUpsellTileWithTracking from 'components/Common/OrderUpsellTileWithTracking'
import { getRemainingSecondsToPostPurchaseInsuranceBookingProtection } from 'lib/order/orderInsuranceBookingProtectionUtils'
import ModalContext from 'contexts/ModalContext'
import RemainingTimeModal from 'components/Insurance/RemainingTimeModal'
import useInsuranceUpsellTileCopy from './useInsuranceUpsellTileCopy'

interface Props {
  insuranceBookingProtectioUpsellData: App.InsuranceUpsell
  onClick: () => void
  order: App.Order
  variant?: 'compact' | 'default'
  onDismiss?: () => void
}

function InsuranceBookingProtectionUpsellTile(props: Props) {
  const {
    insuranceBookingProtectioUpsellData,
    variant = 'default',
    onDismiss,
    order,
    onClick,
  } = props
  const intl = useIntl()
  const { title, description } = useInsuranceUpsellTileCopy('booking-protection')

  const showModal = useContext(ModalContext)

  const {
    fetchingQuotes,
    fetchingLuxPlusSubscription,
    cheapestQuotePrice,
    cheapestQuoteLuxPlusPrice,
    isEnabledLuxPlusDiscountedInsurance,
  } = insuranceBookingProtectioUpsellData

  const remainingTime = useMemo(() => {
    const totalSeconds = getRemainingSecondsToPostPurchaseInsuranceBookingProtection(order)
    const remainingHours = Math.floor(totalSeconds / 3600)
    const remainingMinutes = Math.floor(totalSeconds / 60)
    return {
      hours: remainingHours,
      minutes: remainingMinutes,
    }
  }, [order])

  const alertText = useMemo(() => {
    if (remainingTime.hours >= 1) {
      return intl.formatMessage(
        {
          id: 'jdcNxUgATb3R-x8qnIM7',
          defaultMessage: 'Available for next {hours}h',
          description: 'Alert text showing remaining hours for cancellation protection availability',
        },
        { hours: remainingTime.hours },
      )
    } else if (remainingTime.minutes > 0) {
      return intl.formatMessage(
        {
          id: '4GhdXnrBTt506DkE5AcZ',
          defaultMessage: 'Available for next {minutes}min',
          description: 'Alert text showing remaining minutes for cancellation protection availability',
        },
        { minutes: remainingTime.minutes },
      )
    }
  }, [remainingTime, intl])

  const alertClickHandler = useCallback(() => {
    showModal(<RemainingTimeModal remainingTime={remainingTime} />)
  }, [showModal, remainingTime])

  return <OrderUpsellTileWithTracking
    title={title}
    description={description}
    price={cheapestQuotePrice}
    luxPlusPrice={cheapestQuoteLuxPlusPrice}
    priceUnit="person"
    isPriceLoading={fetchingQuotes || (isEnabledLuxPlusDiscountedInsurance && fetchingLuxPlusSubscription)}
    currencyFormat="dynamicDollar"
    IconComponent={LineShieldCheckIcon}
    analyticsSubject="cfmr"
    className="insurance-upsell-tile"
    variant={variant}
    onDismissClick={onDismiss}
    alertText={alertText}
    onAlertClick={alertClickHandler}
    onClick={onClick}
  />
}

export default InsuranceBookingProtectionUpsellTile
